import React, { useState, useEffect, useCallback,useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import EnhancedPlayerManagementTable from './components/EnhancedPlayerManagementTable';
import { MdDelete, MdEdit, MdAdd, MdError, MdMoreVert } from 'react-icons/md';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddPlayerModal from "./components/AddPlayerModal"
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import Loading from '../../../components/loading/index.jsx';
import TruncatedTeamsList from './components/TruncatedTeamsList';


const KebabMenu = ({ coachId, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <MdMoreVert className="h-6 w-6" />
      </button>
      {isOpen && (
        <div className="absolute right-full top-0 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 mr-2">
          <div className="py-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(coachId);
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <MdEdit className="mr-2" /> Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(coachId);
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <MdDelete className="mr-2" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

  const PlayerManagement = () => {
  const [playerData, setPlayerData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('Player data updated:', playerData);
  }, [playerData]);

  const fetchData = async (url, options = {}) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.log('No token found, redirecting to sign-in');
      navigate('/auth/sign-in');
      return null;
    }

    const headers = new Headers(options.headers || {});
    headers.set('Authorization', `Token ${token}`);

    try {
      const response = await fetch(url, {
        ...options,
        method: options.method || 'GET',
        headers: headers,
        credentials: 'include',
        signal: AbortSignal.timeout(60000)
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/auth/sign-in');
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 500) {
          throw new Error('Server error. The team is working on resolving this issue.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error);
      setError(error.message);
      toast.error(`Failed to fetch data: ${error.message}`);
      return null;
    }
  };

  const fetchPlayerData = async () => {
    setIsLoading(true);
    const playersData = await fetchData('https://staging-sit.com/api/players/');
    if (playersData) {
      const playersWithLoadingTeams = playersData.map(player => ({
        ...player,
        team: { isLoading: true, team: null }
      }));

      setPlayerData(playersWithLoadingTeams);
      
      if (playersData.length > 0) {
        await fetchPlayerTeams(playersData.map(player => player.id));
      }
    }
    setIsLoading(false);
  };

  const fetchPlayerTeams = async (playerIds) => {
    const teamsData = await fetchData(`https://staging-sit.com/api/players/list/team/?player_ids=${playerIds.join(',')}`);
    if (teamsData) {
      console.log('Raw teams data:', teamsData);
      setPlayerData(prevData => {
        const updatedData = prevData.map(player => {
          const playerTeamData = teamsData.find(t => t.player_id === player.id);
          console.log(`Player ${player.id} team data:`, playerTeamData);
          return {
            ...player,
            team: { 
              isLoading: false, 
              team: playerTeamData && playerTeamData.teams.length > 0 ? playerTeamData.teams[0] : null 
            }
          };
        });
        console.log('Updated player data:', updatedData);
        return updatedData;
      });
    } else {
      setPlayerData(prevData => prevData.map(player => ({
        ...player,
        team: { isLoading: false, team: null }
      })));
    }
  };

  useEffect(() => {
    fetchPlayerData();
  }, [retryCount]);

  const openDeleteModal = useCallback((id) => {
    setPlayerToDelete(id);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setPlayerToDelete(null);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    if (!playerToDelete) return;

    setIsDeleting(true);
    const result = await fetchData(`https://staging-sit.com/api/players/${playerToDelete}/`, { method: 'DELETE' });
    if (result !== null) {
      setPlayerData(prevData => prevData.filter(player => player.id !== playerToDelete));
      toast.success("Player deleted successfully");
      closeDeleteModal();
    }
    setIsDeleting(false);
  };

  const handleAddPlayer = async (newPlayerData) => {
    const userData = {
      username: newPlayerData.email,
      email: newPlayerData.email,
      password: newPlayerData.password || 'defaultPassword123',
      fname: newPlayerData.fname,
      lname: newPlayerData.lname,
      user_type: 'player',
    };

    const result = await fetchData('https://staging-sit.com/api/users/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    if (result) {
      console.log('Added player data:', result);
      return result;
    } else {
      throw new Error('Failed to add player');
    }
  };
  
  const handleRowClick = (rowData) => {
    console.log("Clicked row data:", rowData);
  };

  const handleEdit = useCallback((id) => {
    console.log(`Edit player with id: ${id}`);
  }, []);

  const columnsDataColumnsPlayers = [
    {
      Header: "FIRST NAME",
      accessor: "fname",
    },
    {
      Header: "LAST NAME",
      accessor: "lname",
    },
    {
      Header: "EMAIL",
      accessor: "email",
    },
    {
      Header: "TEAM",
      accessor: "team",
      Cell: ({ value }) => {
        console.log('Team cell value:', value);
        if (value.isLoading) {
          return (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
          );
        } else {
          return <TruncatedTeamsList teams={value.team ? [value.team] : []} />;
        }
      },
      className: 'team-column',
      minWidth: 150,
      maxWidth: 300,
    },
    {
      Header: "ACTIONS",
      accessor: "id",
      disableSortBy: true,
      Cell: ({ value }) => (
        <KebabMenu coachId={value} onEdit={handleEdit} onDelete={openDeleteModal} />
      ),
    },
  ];

  if (isLoading) return <Loading />;
  
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden p-6">
        <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-4">Error Occurred</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">{error}</p>
        <div className="space-y-4">
          <button
            onClick={() => setRetryCount(prev => prev + 1)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
          >
            Retry
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('authToken');
              localStorage.removeItem('user');
              navigate('/auth/sign-in');
            }}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
      <div className="p-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Player Management</h2>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#f46524] text-white px-4 py-2 rounded hover:bg-[#f46570] dark:bg-[#7551FF] dark:hover:bg-[#9E8CFC] flex items-center"
        >
          <MdAdd className="mr-2" /> Add New Player
        </button>
      </div>
      <div className="p-4">
        <EnhancedPlayerManagementTable
          columnsData={columnsDataColumnsPlayers}
          tableData={playerData}
          onRowClick={handleRowClick}
        />
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
      <AddPlayerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddPlayer}
        refreshPlayerList={fetchPlayerData}
      />
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default PlayerManagement;